/**
 * UI Store (Zustand)
 *
 * Lightweight state management for client-side UI state.
 * Benefits over Context:
 * - No provider needed
 * - Selector-based subscriptions (only re-renders when selected state changes)
 * - Simple, predictable updates
 * - Persist middleware for localStorage
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types
type View = 'chat' | 'dashboard';
type Theme = 'light' | 'dark' | 'system';
type ModalName = 'createProject' | 'createNote' | 'settings' | 'inviteTeamMember';

interface UIState {
  // View state
  currentView: View;
  setCurrentView: (view: View) => void;

  // Mobile detection
  isMobile: boolean;
  setIsMobile: (mobile: boolean) => void;

  // Modal state
  modals: Record<ModalName, boolean>;
  openModal: (name: ModalName) => void;
  closeModal: (name: ModalName) => void;
  closeAllModals: () => void;

  // Theme
  theme: Theme;
  setTheme: (theme: Theme) => void;

  // Command palette
  showCommandPaletteFilters: boolean;
  setShowCommandPaletteFilters: (show: boolean) => void;
}

// Initial modal state
const initialModals: Record<ModalName, boolean> = {
  createProject: false,
  createNote: false,
  settings: false,
  inviteTeamMember: false,
};

/**
 * Main UI store
 * Persists theme to localStorage
 */
export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // View
      currentView: 'chat',
      setCurrentView: (view) => set({ currentView: view }),

      // Mobile
      isMobile: false,
      setIsMobile: (mobile) => set({ isMobile: mobile }),

      // Modals
      modals: initialModals,
      openModal: (name) =>
        set((state) => ({
          modals: { ...state.modals, [name]: true },
        })),
      closeModal: (name) =>
        set((state) => ({
          modals: { ...state.modals, [name]: false },
        })),
      closeAllModals: () => set({ modals: initialModals }),

      // Theme
      theme: 'system',
      setTheme: (theme) => set({ theme }),

      // Command palette
      showCommandPaletteFilters: true,
      setShowCommandPaletteFilters: (show) => set({ showCommandPaletteFilters: show }),
    }),
    {
      name: 'ui-storage',
      // Only persist these keys
      partialize: (state) => ({
        theme: state.theme,
        showCommandPaletteFilters: state.showCommandPaletteFilters,
      }),
    },
  ),
);

// Convenience selectors (for optimized re-renders)
export const useTheme = () => useUIStore((state) => state.theme);
export const useSetTheme = () => useUIStore((state) => state.setTheme);
export const useShowCommandPaletteFilters = () =>
  useUIStore((state) => state.showCommandPaletteFilters);
export const useSetShowCommandPaletteFilters = () =>
  useUIStore((state) => state.setShowCommandPaletteFilters);
export const useModals = () => useUIStore((state) => state.modals);
export const useOpenModal = () => useUIStore((state) => state.openModal);
export const useCloseModal = () => useUIStore((state) => state.closeModal);
